import { resetPDFReport } from 'modules/messagingInsights/actions';
import { FETCH_ERRORS as e } from 'modules/messagingInsights/constants';
import {
  selectError,
  selectPDFReportLoading,
  selectPDFReportUrl,
} from 'modules/messagingInsights/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { PDFGenerationDialog } from './PDFGenerationDialog';
import { PDFReportForm } from './PDFReportForm';

type Props = {
  isPDFReportModalOpen: boolean;
  onClose: () => void;
};

export const PDFReportModal = ({ isPDFReportModalOpen, onClose }: Props) => {
  const dispatch = useDispatch();
  const errors = useSelector(selectError) || {};
  const pdfReportFetchError = Object.keys(errors).includes(e.FETCH_PDF_REPORT);
  const pdfReportLoading = useSelector(selectPDFReportLoading);
  const pdfReportUrl = useSelector(selectPDFReportUrl);
  const isPDFReportCreated = pdfReportUrl.length > 0;
  const isDefaultState = !pdfReportFetchError && !pdfReportLoading && pdfReportUrl.length === 0;

  const modalTitle = () => {
    switch (true) {
      case pdfReportFetchError:
        return 'PDF generation error';
      case pdfReportLoading:
        return 'Generating your PDF...';
      case isPDFReportCreated:
        return 'PDF report created';
      default:
        return 'New PDF report';
    }
  };

  const modalBody = () => {
    switch (true) {
      case pdfReportFetchError:
        return (
          <PDFGenerationDialog
            status="error"
            onRetry={() => dispatch(resetPDFReport(pdfReportUrl))}
          />
        );
      case pdfReportLoading:
        return <PDFGenerationDialog status="loading" />;
      case isPDFReportCreated:
        return <PDFGenerationDialog status="success" downloadLink={pdfReportUrl} />;
      default:
        return <PDFReportForm />;
    }
  };

  return (
    <Modal
      id="exportPDFReportModal"
      isModalOpen={isPDFReportModalOpen}
      onClose={pdfReportLoading ? () => null : onClose}
      size={isDefaultState ? MODAL_SIZES.LARGE : MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{modalTitle()}</ModalHeader>
      <ModalBody>{modalBody()}</ModalBody>
      {isDefaultState && (
        <ModalFooter>
          <Button
            data-test="create-report-button"
            type="submit"
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            Create Report
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};
